import { NoDataComponent } from '../NoDataComponent';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { getHandover, useGetAllShortPassports, useGetHandoverProtocols as getHandoverProtocols } from '../../utils/warehouses-hooks';
import { HandoverProtocol } from '../../models/table-models';
import { useDeleteProtocol } from '../../utils/clients-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { InfoModal } from '../InfoModal';
import dateFormat from 'dateformat';
import { DashboardProtocol } from './DashboardProtocol';
import { HandoverProps } from '../../models/warehouse-models';
import { transformDateToString } from '../../utils/helper-functions';
import { TableRequestParams } from '../../utils/data-hooks';
import { AddActionComponent } from '../AddActionComponent';
import { AddInvoice } from '../../pages/AddInvoice';
import { Role } from '../../utils/Enums/Roles';

export const Protocols = ({ branchId, refetchEntitiesHook }: {
    branchId: number;
    refetchEntitiesHook: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [revision, setRevision] = useState(0);
    const [result, setResult] = useState<{ branchId: number; revision: number; items: HandoverProtocol[] }>();
    const [failed, setFailed] = useState(false);
    const fetchHook = useCallback(() => {
        setRevision(value => value + 1);
    }, []);

    useEffect(() => {
        let active = true;
        setFailed(false);
        getHandoverProtocols({} as TableRequestParams, branchId).then(({ items }) => {
            if (active) setResult({ branchId, revision, items });
        }).catch(error => {
            if (active) {
                console.error('Failed to load branch protocols', error);
                setFailed(true);
            }
        });
        return () => { active = false; };
    }, [branchId, revision]);

    const current = result && result.branchId === branchId && result.revision === revision ? result : undefined;
    const items = current?.items ?? [];

    return (
        <div className={'d-flex'}>
            <AddActionComponent
                title={t('addPayment')}
                addComponent={AddInvoice}
                accessActionRoles={[Role.FINANCIAL, Role.ADMIN, Role.COORDINATOR]}
                {...{ refetchEntitiesHook }}
            />
            <div className='align-self-end mb-4'>
                <h5 className={'mb-3 text-center'}>{t('protocols') + ':'}</h5>
                <div className={'w-100 border border-dark'}>
                    {!current ? (
                        <div className='d-flex align-items-center justify-content-center px-2' style={{ width: 280, height: '20vh' }}>
                            {failed
                                ? <Button size='sm' onClick={fetchHook}>{t('retryProtocols')}</Button>
                                : <Spinner animation='border' role='status' />}
                        </div>
                    ) : items.length > 0 ? (
                        <>
                            <div className={'wrapper-invoices'}>
                                {items.map((protocol, index) => (
                                    <Protocol
                                        {...{ protocol, fetchHook }}
                                        key={index}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className='d-flex align-items-center px-2' style={{ width: 280, height: '20vh' }}>
                            <NoDataComponent noDataText={t('protocols')}></NoDataComponent>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const Protocol = ({
                             protocol,
                             fetchHook,
                         }: {
    protocol: HandoverProtocol;
    fetchHook: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [getPassports] = useAsyncResource(useGetAllShortPassports, false);
    const passports = getPassports();

    const [showModal, setShowModal] = useState(false);
    const [forDelete, setForDelete] = useState(false);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const submitRef = useRef<HTMLButtonElement>(null);
    const toggleShowModal = () => {
        setShowModal((prev) => !prev);
    };

    const handleSubmit = async () => {
        setLoading(true);

        const handoverData: HandoverProps = {
            id: protocol.id,
            date: transformDateToString(protocol.date, 'dd.mm.yyyy'),
            town: protocol.city,
            representative: protocol.clientRepresentative,
            taskItemsVO: {
                branchId: protocol.branchId,
                userId: protocol.distributorId,
                neededItems: protocol.items,
                taskId: 1,
                taskCategory: protocol.taskCategory,
            },
            save: false,
            signature: protocol.signature,
        };
        const pdfFile = await getHandover(handoverData);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            setLoading(false);
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            setLoading(false);
            return false;
        }
    };

    const DeleteProtocol = async (fields: HandoverProtocol) => {
        await useDeleteProtocol(fields, comment);
        setShowModal(false);
        fetchHook();

        toast.success(<CustomToastContent content={[t('protocolDeleted')]} />, toastOptionsTopRight);
    };

    return (
        <>
            <div
                className={`d-flex w-100 p-1 py-2 align-items-center border-bottom border-dark hover`}
                onClick={toggleShowModal}
            >
                <div className='w-100 d-flex align-items-center justify-content-between'>
                    <span className='text-secondary'>
                        {dateFormat(protocol.date, 'dd.mm.yyyy')} {dateFormat(protocol.date, 'HH:MM:ss')}
                    </span>
                    {/*<FontAwesomeIcon icon={faCircle} {...{ color }} className='mx-1'></FontAwesomeIcon>*/}
                </div>
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title
                        id='contained-modal-title-vcenter'>{t('Details for') + ' ' + t('handover')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <DashboardProtocol item={protocol} />
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                    <Button className='btn-danger' onClick={() => setForDelete(true)}>{t('delete')}</Button>
                    {loading ? <Spinner animation={'border'}></Spinner> :
                        <Button ref={submitRef} form='form' type='submit' onClick={handleSubmit}>{t('print')}</Button>}
                </Modal.Footer>
            </Modal>
            {forDelete && <InfoModal
                isDisabled={comment.length < 1}
                show={forDelete}
                onHide={(isDelete: boolean) => {
                    if (isDelete) {
                        DeleteProtocol(protocol);
                    }
                    setComment('');
                    return setForDelete(false);
                }}
            >
                <span className='w-100 text-secondary'>{`*${t('commentRequired')}`}</span>
                <Form.Control
                    placeholder={t('comment')}
                    name={'freetext'}
                    onChange={(e) => setComment(e.currentTarget.value)}
                    value={comment}
                />
            </InfoModal>}
        </>
    );
};
